import { Link } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { cn } from '@localagent/ui/lib/utils';
import { PRIORITY_COLORS, PRIORITY_LABELS } from '../../lib/work-management-helpers';
import { workItemSessionsAtom } from '../../stores/work/atoms';
import type { Ticket } from '@localagent/sdk';

interface TicketCardProps {
  ticket: Ticket;
}

export function TicketCard({ ticket }: TicketCardProps) {
  const link = useAtomValue(workItemSessionsAtom)[ticket.id];

  return (
    <div
      data-testid={`ticket-card-${ticket.id}`}
      data-project-id={ticket.project_id ?? ''}
      data-session-attached={link ? 'true' : 'false'}
      data-session-running={link?.running ? 'true' : 'false'}
      className="rounded-md bg-background p-3 shadow-sm hover:shadow-md transition-shadow"
    >
      <Link
        to={`tickets/${ticket.id}`}
        draggable={false}
        onClick={(e) => e.stopPropagation()}
        data-testid={`ticket-detail-link-${ticket.id}`}
        className="mb-1 block"
      >
        <span className="text-sm font-medium hover:underline">{ticket.title}</span>
        <code className="block text-xs text-muted-foreground hover:underline">
          {ticket.identifier}
        </code>
      </Link>
      <div className="flex flex-wrap items-center gap-2">
        {!ticket.project_id && (
          <span
            data-testid={`ticket-no-project-${ticket.id}`}
            className="inline-flex items-center rounded-full bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800"
          >
            No project
          </span>
        )}
        {ticket.priority !== 'none' && (
          <span
            className={cn(
              'inline-flex items-center rounded-full px-1.5 py-0.5 text-xs font-medium',
              PRIORITY_COLORS[ticket.priority]
            )}
          >
            {PRIORITY_LABELS[ticket.priority]}
          </span>
        )}
      </div>
      {link && (
        <Link
          to={`/chat/${link.sessionId}`}
          draggable={false}
          onClick={(e) => e.stopPropagation()}
          title={link.running ? 'Open the session working on this ticket' : 'Open the session attached to this ticket'}
          data-testid={`ticket-session-link-${ticket.id}`}
          className={cn(
            'mt-2 inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium transition-colors',
            link.running
              ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
              : 'bg-primary/10 text-primary hover:bg-primary/20'
          )}
        >
          {link.running && (
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-yellow-600" aria-hidden />
          )}
          {link.running ? 'Session running' : 'Session attached'}
        </Link>
      )}
    </div>
  );
}
